import { useEffect, useState, useMemo } from 'react';
import api from '../lib/api';
import { BadgeIcon } from './BadgeIcons';
import { Award, CheckCircle2, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import BadgeEarnedModal from './BadgeEarnedModal';
import PeakCelebration from './PeakCelebration';
import { useAuth } from '../contexts/AuthContext';
import './BadgesSection.css';

const INITIAL_VISIBLE = 6;

/**
 * Reader Badges section. Sits below the castle and never replaces it.
 * Ordering: earned first (newest first) → then unearned by highest progress.
 * Collapse: shows only INITIAL_VISIBLE cards by default with a toggle.
 * Celebrations:
 *   - First time the reader reaches peak: full-screen peak celebration.
 *   - Any newly earned badge (compared to last-seen list): premium badge modal.
 */
const BadgesSection = ({ atPeak = false }) => {
  const { user } = useAuth();
  const rn = user?.reader_number;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [pendingEarned, setPendingEarned] = useState([]); // queue of newly earned badges
  const [showPeak, setShowPeak] = useState(false);

  useEffect(() => {
    if (!rn) return;
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/badges');
        if (!alive) return;
        setData(res.data);
        // Detect newly earned badges vs last-seen list stored per-reader
        const seenKey = `badges_seen_${rn}`;
        const seen = JSON.parse(localStorage.getItem(seenKey) || '[]');
        const currentEarnedKeys = res.data.badges.filter((b) => b.earned).map((b) => b.key);
        const newlyEarned = res.data.badges.filter((b) => b.earned && !seen.includes(b.key));
        if (newlyEarned.length > 0) {
          setPendingEarned(newlyEarned);
        }
        localStorage.setItem(seenKey, JSON.stringify(currentEarnedKeys));
      } catch {
        // Silent — badges are non-critical
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [rn]);

  // Peak celebration — once per reader per browser
  useEffect(() => {
    if (!atPeak || !rn) return;
    const key = `peak_seen_${rn}`;
    if (!localStorage.getItem(key)) {
      setShowPeak(true);
      localStorage.setItem(key, '1');
    }
  }, [atPeak, rn]);

  // Sorted list: earned first → then by progress desc
  const sortedBadges = useMemo(() => {
    if (!data?.badges) return [];
    const arr = [...data.badges];
    arr.sort((a, b) => {
      if (a.earned && !b.earned) return -1;
      if (!a.earned && b.earned) return 1;
      if (a.earned && b.earned) {
        // both earned — most recently earned first
        return (b.earned_at || '').localeCompare(a.earned_at || '');
      }
      // both unearned — highest progress first
      if (b.progress_percent !== a.progress_percent) return b.progress_percent - a.progress_percent;
      return a.order - b.order;
    });
    return arr;
  }, [data]);

  if (loading) return null;
  if (!data || !data.at_peak || !sortedBadges.length) return null;

  const { earned_count, total, next } = data;
  const visibleBadges = expanded ? sortedBadges : sortedBadges.slice(0, INITIAL_VISIBLE);
  const hiddenCount = Math.max(0, sortedBadges.length - INITIAL_VISIBLE);

  const dismissEarnedModal = () => {
    setPendingEarned((prev) => prev.slice(1));
  };

  return (
    <section className="badges-section" data-testid="badges-section" dir="rtl">
      {atPeak && (
        <div className="peak-ribbon" data-testid="peak-ribbon">
          <div className="peak-ribbon-inner">
            <div className="peak-ribbon-icon">
              <BadgeIcon name="olive" />
            </div>
            <div className="peak-ribbon-text">
              <div className="peak-ribbon-title">بلغت قمة القلاع</div>
              <div className="peak-ribbon-sub">وما بعد القمة أثر ما تقرأ</div>
            </div>
          </div>
        </div>
      )}

      <header className="badges-header">
        <div className="badges-header-left">
          <Award className="badges-header-icon" />
          <div>
            <h2 className="badges-title">أوسمة القارئ</h2>
            <p className="badges-subtitle">كل وسام يعكس خطوة في رحلتك المعرفية</p>
          </div>
        </div>
        <div className="badges-count" data-testid="badges-count">
          <span className="badges-count-num">{earned_count}</span>
          <span className="badges-count-sep">/</span>
          <span className="badges-count-total">{total}</span>
        </div>
      </header>

      {hiddenCount > 0 && (
        <div className="badges-toggle-wrap">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="badges-toggle-btn"
            data-testid="toggle-all-badges"
          >
            {expanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                <span>إخفاء البقية</span>
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                <span>عرض جميع الأوسمة ({total})</span>
              </>
            )}
          </button>
        </div>
      )}

      <div className="badges-grid">
        {visibleBadges.map((b) => (
          <div
            key={b.key}
            className={`badge-card ${b.earned ? 'is-earned' : 'is-locked'}`}
            data-testid={`badge-${b.key}`}
            title={b.description}
          >
            <div className="badge-crest-wrap">
              <div className="badge-crest">
                <BadgeIcon name={b.icon} />
              </div>
              {b.earned && (
                <span className="badge-check" aria-hidden>
                  <CheckCircle2 className="w-4 h-4" />
                </span>
              )}
            </div>
            <div className="badge-name">{b.name}</div>
            <div className="badge-desc">{b.description}</div>
            {b.earned ? (
              <div className="badge-earned-tag">مكتمل</div>
            ) : (
              <>
                <div className="badge-progress" aria-label={`تقدم ${b.progress_percent}%`}>
                  <div className="badge-progress-bar" style={{ width: `${b.progress_percent}%` }} />
                </div>
                <div className="badge-progress-label">
                  {b.current} / {b.target} {b.condition_type === 'streak_days' ? 'يوم' : b.condition_type === 'total_pages' ? 'صفحة' : b.condition_type === 'books_completed' ? 'كتب' : b.condition_type === 'variety' ? 'مجالات' : ''}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {next && !expanded && (
        <div className="next-badge-card" data-testid="next-badge">
          <div className="next-badge-icon">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="next-badge-body">
            <div className="next-badge-eyebrow">إنجازك التالي</div>
            <div className="next-badge-title">{next.name}</div>
            <div className="next-badge-desc">{next.description}</div>
            <div className="next-badge-progress">
              <div className="next-badge-progress-bar" style={{ width: `${next.progress_percent}%` }} />
            </div>
            <div className="next-badge-progress-label">{next.current} / {next.target}</div>
          </div>
        </div>
      )}

      <p className="badges-tagline">✦ اقرأ، لترتقي، واثبت لتحصد الثمر</p>

      {/* Overlays */}
      {showPeak && <PeakCelebration onClose={() => setShowPeak(false)} />}
      {pendingEarned.length > 0 && (
        <BadgeEarnedModal badge={pendingEarned[0]} onClose={dismissEarnedModal} />
      )}
    </section>
  );
};

export default BadgesSection;
